import React from 'react';
import {Box, Button, Typography} from '@mui/material';
import {useSubmitHandover} from '../../hooks/useSubmitHandover';

import {themeColors} from '../../theme/Theme';
import {useOVRecommends} from '../../hooks/useOVRecommends';
import {HandoverSubmitValidationChecks} from './HandoverSubmitValidationChecks';
import {useReviewDataValidation} from '../../hooks/useClientDataValidation';
import Loading from '../../components/Loading/Loading';
import {useReviewContext} from '../../contexts/ReviewContext';
import {HandoverValidationMessage} from '../../components/FooterNavigation/HandoverValidationMessage';
import {KeyClientDataBanner} from '../../components/KeyClientDataBanner/KeyClientDataBanner';

type Props = {
  onClose: () => void;
  onNoRequirementsConfirm?: () => Promise<void> | void;
};

export const HandoverSFPNonPADModal: React.FC<Props> = ({
  onClose: _onClose,
  onNoRequirementsConfirm,
}) => {
  const {data: recsData, loading} = useOVRecommends();
  const {allValid} = useReviewContext();
  const {softSubmit, setSubmissionStatus} = useSubmitHandover();
  const {reviewDataIssues, hasReviewDataIssues} = useReviewDataValidation();
  const canFullSubmit = allValid && !hasReviewDataIssues;

  if (loading) {
    return <Loading />;
  }

  return (
    <>
      <HandoverSubmitValidationChecks onNoRequirementsConfirm={onNoRequirementsConfirm} />
      {hasReviewDataIssues && (
        <Box p={2}>
          {/* This modal is SFP-only, so the eCQ prompt is always shown */}
          <KeyClientDataBanner issues={reviewDataIssues} isSFP />
        </Box>
      )}
      <Box display="flex" p={2}>
        <Box display="flex" flexDirection="column" alignItems="baseline" mr={2}>
          <Button
            variant="contained"
            color="primary"
            style={{marginRight: '2rem'}}
            onClick={() => softSubmit()}
          >
            Soft Submission
          </Button>
          <Box mt={1}>
            <Typography>
              Submit my handover notes without the requirements. I will come back and
              finalise these.
            </Typography>
          </Box>
        </Box>
        <Box display="flex" flexDirection="column" alignItems="baseline">
          <Button
            variant="contained"
            color="primary"
            style={{marginRight: '2rem'}}
            disabled={!canFullSubmit}
            onClick={() => {
              const finalisedRequirementsLength =
                recsData?.ovrecommends?.filter((x) => x.isFinalised)?.length || 0;
              const unfinalisedRequirementsLength =
                recsData?.ovrecommends?.filter((x) => !x.isFinalised)?.length || 0;
              // There are no requirements on this review. Are you happy to full submit?
              if (finalisedRequirementsLength + unfinalisedRequirementsLength === 0) {
                setSubmissionStatus('hasNoRequirements');
                return;
              }
              if (unfinalisedRequirementsLength > 0) {
                setSubmissionStatus('unfinalisedRequirements');
                return;
              }
              setSubmissionStatus('allRequirementsFinalised');
            }}
          >
            Full Submission
          </Button>
          <Box mt={1}>
            <Typography>
              Submit my handover notes with the requirements which are finalised.
            </Typography>
          </Box>
          {!allValid && <HandoverValidationMessage />}
          <Box bgcolor={themeColors.lightWarning} borderRadius="2px" padding={2} mt={1}>
            <Typography variant="caption">
              Please note once you have fully submitted the handover notes, you will no
              longer be able to edit the requirements.
            </Typography>
          </Box>
        </Box>
      </Box>
    </>
  );
};
